import React from "react";
import { DragDropProvider, type DragEndEvent } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { move } from "@dnd-kit/helpers";

import QuestionAdd from "./QuestionAdd";
import QuestionPreview from "./QuestionPreview";
import QuestionEditor from "./QuestionEditor";
import SurveyQuestionTypeOptions from "./SurveyQuestionTypeOptions";
import type { SurveyQuestion } from "../../types/survey.types";
import type { SurveyQuestionType } from "../../data";
import { Card } from "../../../../components/card";
import { FormErrorText } from "../../../../components/form/FormErrorText";
import type {
  ChoiceOptionErrorMessage,
  DateRangeErrorMessage,
  ScaleLabelErrorMessage,
  YesNoLabelErrorMessage,
} from "./question-authoring/shared";

export interface QuestionCardActions {
  add: (type: SurveyQuestionType) => string;
  duplicate: (index: number) => string;
  delete: (index: number) => void;
  update: (index: number, updates: Partial<SurveyQuestion>) => void;
  move: (index: number, direction: -1 | 1) => void;
  reorder: (nextQuestions: SurveyQuestion[]) => void;
}

export interface QuestionCardValidation {
  message?: string;
  questionMessages?: readonly (string | undefined)[];
  optionMessages?: readonly (readonly ChoiceOptionErrorMessage[] | undefined)[];
  scaleLabelMessages?: readonly (ScaleLabelErrorMessage | undefined)[];
  dateRangeMessages?: readonly (DateRangeErrorMessage | undefined)[];
  yesNoLabelMessages?: readonly (YesNoLabelErrorMessage | undefined)[];
}

interface QuestionCardProps {
  questions: SurveyQuestion[];
  isPreviewMode: boolean;
  actions: QuestionCardActions;
  validation?: QuestionCardValidation;
  lastAddedQuestionId?: string | null;
  disabled?: boolean;
}

export function QuestionCard({
  questions,
  isPreviewMode,
  actions,
  validation,
  lastAddedQuestionId,
  disabled = false,
}: QuestionCardProps) {
  const [selectedQuestionId, setSelectedQuestionId] = React.useState<string | null>(null);
  const questionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const getQuestionId = React.useCallback(
    (question: SurveyQuestion) => question.client_id ?? `question-${question.position}`,
    [],
  );

  const handleQuestionRefChange = React.useCallback(
    (id: string, element: HTMLDivElement | null) => {
      questionRefs.current[id] = element;
    },
    [],
  );

  const handleAddQuestion = (type: SurveyQuestionType) => {
    const newQuestionId = actions.add(type);
    setSelectedQuestionId(newQuestionId);
  };

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (event.canceled) {
        return;
      }

      const sortableQuestions = questions.map((question) => ({
        id: getQuestionId(question),
        question,
      }));
      const reorderedQuestions = move(sortableQuestions, event).map(({ question }) => question);
      actions.reorder(reorderedQuestions);
    },
    [actions, getQuestionId, questions],
  );

  React.useEffect(() => {
    if (selectedQuestionId === null) {
      return;
    }

    if (questions.length === 0) {
      setSelectedQuestionId(null);
      return;
    }

    const exists = questions.some((question) => getQuestionId(question) === selectedQuestionId);
    if (!exists) {
      setSelectedQuestionId(null);
    }
  }, [questions, selectedQuestionId, getQuestionId]);

  React.useEffect(() => {
    if (!lastAddedQuestionId) {
      return;
    }

    const questionElement = questionRefs.current[lastAddedQuestionId];
    if (!questionElement) {
      return;
    }

    questionElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    window.setTimeout(() => {
      const questionInput = questionElement.querySelector(
        `input[id="question-${lastAddedQuestionId}"]`,
      ) as HTMLInputElement | null;

      questionInput?.focus();
    }, 300);
  }, [lastAddedQuestionId]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between sticky top-[calc(81px)] bg-white z-30">
        <h3 className="text-lg font-semibold">Questions</h3>
        {disabled && (
          <div className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 px-3 py-2 rounded-md">
            Questions cannot be edited for published surveys
          </div>
        )}

        <QuestionAdd
          onAddQuestion={handleAddQuestion}
          disabled={disabled}
        />
      </div>

      <FormErrorText message={validation?.message} />

      {isPreviewMode ? (
        <QuestionPreview questions={questions} />
      ) : (
        <DragDropProvider onDragEnd={handleDragEnd}>
          <div className="space-y-4">
            {questions.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="p-6">
                  <div className="text-center py-8 border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700/50">
                    <div className="text-gray-400 dark:text-gray-500">
                      <p className="text-sm font-semibold">No questions yet</p>
                      <p className="text-xs mt-1">Click "Add Question" to get started</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              questions.map((question, index) => {
                const questionId = getQuestionId(question);

                return (
                  <SortableQuestionItem
                    key={questionId}
                    question={question}
                    index={index}
                    questionId={questionId}
                    isSelected={selectedQuestionId === questionId}
                    onSelect={() => setSelectedQuestionId(questionId)}
                    onDuplicate={() => setSelectedQuestionId(actions.duplicate(index))}
                    onDelete={() => actions.delete(index)}
                    onUpdateQuestion={(updates) => actions.update(index, updates)}
                    onMoveUp={index > 0 ? () => actions.move(index, -1) : undefined}
                    onMoveDown={
                      index < questions.length - 1 ? () => actions.move(index, 1) : undefined
                    }
                    questionErrorMessage={validation?.questionMessages?.[index]}
                    optionErrorMessages={validation?.optionMessages?.[index]}
                    scaleLabelErrorMessage={validation?.scaleLabelMessages?.[index]}
                    dateRangeErrorMessage={validation?.dateRangeMessages?.[index]}
                    yesNoLabelErrorMessage={validation?.yesNoLabelMessages?.[index]}
                    onQuestionRefChange={(element) => handleQuestionRefChange(questionId, element)}
                  />
                );
              })
            )}
          </div>
        </DragDropProvider>
      )}
    </div>
  );
}

interface SortableQuestionItemProps {
  question: SurveyQuestion;
  index: number;
  questionId: string;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onUpdateQuestion: (updates: Partial<SurveyQuestion>) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  questionErrorMessage?: string;
  optionErrorMessages?: readonly ChoiceOptionErrorMessage[];
  scaleLabelErrorMessage?: ScaleLabelErrorMessage;
  dateRangeErrorMessage?: DateRangeErrorMessage;
  yesNoLabelErrorMessage?: YesNoLabelErrorMessage;
  onQuestionRefChange: (element: HTMLDivElement | null) => void;
}

function SortableQuestionItem({
  question,
  index,
  questionId,
  isSelected,
  onSelect,
  onDuplicate,
  onDelete,
  onUpdateQuestion,
  onMoveUp,
  onMoveDown,
  questionErrorMessage,
  optionErrorMessages,
  scaleLabelErrorMessage,
  dateRangeErrorMessage,
  yesNoLabelErrorMessage,
  onQuestionRefChange,
}: SortableQuestionItemProps) {
  const { ref, handleRef } = useSortable({
    id: questionId,
    index,
  });

  return (
    <Card
      ref={ref}
      className="space-y-4 p-5"
    >
      <QuestionEditor
        question={question}
        index={index}
        isSelected={isSelected}
        onSelect={onSelect}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onUpdateQuestion={onUpdateQuestion}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        questionErrorMessage={questionErrorMessage}
        onQuestionRefChange={onQuestionRefChange}
        onDragHandleRefChange={handleRef}
      />
      <SurveyQuestionTypeOptions
        question={question}
        index={index}
        isPreview={false}
        optionErrorMessages={optionErrorMessages}
        scaleLabelErrorMessage={scaleLabelErrorMessage}
        dateRangeErrorMessage={dateRangeErrorMessage}
        yesNoLabelErrorMessage={yesNoLabelErrorMessage}
        onUpdateQuestion={(nextQuestion) => onUpdateQuestion({ data: nextQuestion.data })}
      />
    </Card>
  );
}

export default QuestionCard;
